import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const DiagnosticPage = () => {
  const [diagnostics, setDiagnostics] = useState({
    localStorage: 'checking...',
    firebase: 'checking...',
    network: 'checking...',
    auth: 'checking...'
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const runDiagnostics = async () => {
      const results = {};

      // Check localStorage
      try {
        const lawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
        results.localStorage = `✅ Working - ${lawyers.length} lawyers cached`;
      } catch (error) {
        results.localStorage = `❌ Error: ${error.message}`;
      }

      // Check Firebase
      try {
        const { db } = await import('../config/firebase');
        if (db) {
          results.firebase = '✅ Firebase configured';
        } else {
          results.firebase = '❌ Firebase not configured';
        }
      } catch (error) {
        results.firebase = `❌ Firebase error: ${error.message}`;
      }

      // Check network
      try {
        const response = await fetch('http://localhost:3010');
        if (response.ok) {
          results.network = '✅ Server responding';
        } else {
          results.network = `❌ Server error: ${response.status}`;
        }
      } catch (error) {
        results.network = `❌ Network error: ${error.message}`;
      }

      // Check auth
      try {
        const { auth } = await import('../config/firebase');
        if (auth) {
          results.auth = '✅ Auth configured';
        } else {
          results.auth = '❌ Auth not configured';
        }
      } catch (error) {
        results.auth = `❌ Auth error: ${error.message}`;
      }

      setDiagnostics(results);
      setLoading(false);
    };

    runDiagnostics();
  }, []);

  const clearCache = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  const goToFindLawyer = () => {
    navigate('/find-lawyer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Running diagnostics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">🔍 App Diagnostics</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">System Status</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">LocalStorage:</span>
              <span className={diagnostics.localStorage.includes('✅') ? 'text-green-600' : 'text-red-600'}>
                {diagnostics.localStorage}
              </span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Firebase:</span>
              <span className={diagnostics.firebase.includes('✅') ? 'text-green-600' : 'text-red-600'}>
                {diagnostics.firebase}
              </span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Network:</span>
              <span className={diagnostics.network.includes('✅') ? 'text-green-600' : 'text-red-600'}>
                {diagnostics.network}
              </span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Authentication:</span>
              <span className={diagnostics.auth.includes('✅') ? 'text-green-600' : 'text-red-600'}>
                {diagnostics.auth}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={clearCache}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              🗑️ Clear All Cache
            </button>
            
            <button
              onClick={goToFindLawyer}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              🔍 Go to Find Lawyer
            </button>
            
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              🔄 Reload Page
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              🏠 Go to Home
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Troubleshooting Steps</h2>
          
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>If any status shows ❌, try clicking "Clear All Cache"</li>
            <li>If the app is stuck loading, try "Reload Page"</li>
            <li>If Find Lawyer doesn't work, try going to Home first</li>
            <li>Check the browser console for any error messages</li>
            <li>Try opening the app in an incognito/private window</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticPage;
